/**
 * Sparkline — minimal SVG line chart for HUD KPI cards.
 * Zero dependency. Auto-scales to the max value. Renders polyline + soft area.
 *
 * Props:
 *   data: number[]    — series points (oldest first, newest last)
 *   color: string     — stroke + area color
 *   height: number    — pixel height (default 28)
 *   testid: string    — data-testid hook
 */
const Sparkline = ({ data = [], color = '#D9A05B', height = 28, testid }) => {
  if (!data || data.length === 0) {
    return <div className="h-7 opacity-30" data-testid={testid} />;
  }
  const w = 100; // viewBox width — preserveAspectRatio=none so it stretches
  const h = 100;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const stepX = w / Math.max(data.length - 1, 1);
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = h - ((v - min) / range) * h * 0.85 - h * 0.075; // 7.5% padding top/bottom
    return [x, y];
  });
  const polyline = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `M0,${h} L${polyline.split(' ').join(' L')} L${w},${h} Z`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ height, width: '100%', display: 'block' }}
      data-testid={testid}
    >
      <defs>
        <linearGradient id={`spark-grad-${testid || color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#spark-grad-${testid || color.replace('#', '')})`} />
      <polyline
        points={polyline}
        fill="none"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* end-of-line dot */}
      {points.length > 0 && (
        <circle
          cx={points[points.length - 1][0]}
          cy={points[points.length - 1][1]}
          r="2.2"
          fill={color}
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
};

export default Sparkline;
